import type { CollectionQuery, Folder } from '@/services/api';

export interface QueryWithPath extends CollectionQuery {
  collectionId: string;
  folderId?: string;
}

/**
 * Recursively collects all query IDs from a collection in tree order
 */
export function collectAllQueryIds(
  queries: CollectionQuery[],
  folders: Folder[],
  parentFolderId?: string
): string[] {
  const result: string[] = [];
  
  // Helper to collect queries from a specific folder
  const collectFromFolder = (folderId?: string) => {
    // Add queries from this folder/root
    const folderQueries = queries.filter(q => 
      folderId ? q.folderId === folderId : !q.folderId
    );
    for (const query of folderQueries) {
      if (query.id) {
        result.push(query.id);
      }
    }
    
    // Then process subfolders
    const subfolders = folders.filter(f => 
      folderId ? f.parentFolderId === folderId : !f.parentFolderId
    );
    for (const subfolder of subfolders) {
      if (subfolder.id) {
        collectFromFolder(subfolder.id);
      }
    }
  };
  
  if (!parentFolderId) {
    // At root level, collect ALL queries from the entire tree
    collectFromFolder(undefined);
  } else {
    // Collect from specific folder
    collectFromFolder(parentFolderId);
  }

  return result;
}

/**
 * Collects all query IDs from multiple collections
 */
export function collectAllQueryIdsFromCollections(
  collectionsData: {
    queries: CollectionQuery[];
    folders: Folder[];
  }[]
): string[] {
  const result: string[] = [];
  
  for (const data of collectionsData) {
    result.push(...collectAllQueryIds(data.queries, data.folders));
  }
  
  return result;
}